import { useState } from "react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { FileText, Download, Presentation, Loader2, ChevronDown, Image } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import html2canvas from "html2canvas";

interface ExportControlsProps {
  onExportPDF?: () => Promise<void> | void;
  onExportPowerPoint?: () => Promise<void> | void;
  showPowerPoint?: boolean;
  showImageExport?: boolean;
  resultData?: any;
  moduleType?: string;
  isPublicView?: boolean;
  className?: string;
  variant?: "default" | "outline" | "secondary";
  size?: "default" | "sm" | "lg";
}

export default function ExportControls({
  onExportPDF,
  onExportPowerPoint,
  showPowerPoint = false,
  showImageExport = true,
  resultData,
  moduleType,
  isPublicView = false,
  className = "",
  variant = "outline",
  size = "default"
}: ExportControlsProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<'pdf' | 'powerpoint' | 'image' | null>(null);
  const { toast } = useToast();

  const handleImageExport = async () => {
    if (isExporting) return;

    setIsExporting(true);
    setExportType('image');

    try {
      // Find the main content area to capture
      const contentElement = document.querySelector('[data-export-content]') || 
                           document.querySelector('.max-w-7xl') ||
                           document.querySelector('main') ||
                           document.body;

      if (!contentElement) {
        throw new Error('Could not find content to export');
      }

      // Configure html2canvas for high quality capture
      const canvas = await html2canvas(contentElement as HTMLElement, {
        backgroundColor: '#1e293b', // Dark background to match the design
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        removeContainer: true,
        letterRendering: true,
        logging: false,
        width: contentElement.scrollWidth,
        height: contentElement.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Failed to generate image');
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `${moduleType || 'assessment'}-results-${timestamp}.png`;
        link.download = filename;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast({
          title: "Image Export Complete",
          description: `Your ${moduleType || 'assessment'} results have been saved as an image.`,
        });
      }, 'image/png', 0.95);

    } catch (error) {
      console.error('Error exporting image:', error);
      toast({
        title: "Image Export Failed",
        description: "Failed to export image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const handleExport = async (type: 'pdf' | 'powerpoint' | 'image') => {
    if (isExporting) return;

    if (type === 'image') {
      await handleImageExport();
      return;
    }

    setIsExporting(true);
    setExportType(type);

    try {
      if (type === 'pdf' && onExportPDF) {
        await onExportPDF();
        toast({
          title: "PDF Export Complete",
          description: "Your report has been downloaded successfully.",
        });
      } else if (type === 'powerpoint' && onExportPowerPoint) {
        await onExportPowerPoint();
        toast({
          title: "PowerPoint Export Complete", 
          description: "Your presentation has been downloaded successfully.",
        });
      }
    } catch (error) {
      console.error(`Error exporting ${type}:`, error);
      toast({
        title: "Export Failed",
        description: `Failed to export ${type}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  // Single export button or dropdown based on options available
  if (!showPowerPoint && !showImageExport) {
    return (
      <Button
        onClick={() => handleExport('pdf')}
        disabled={isExporting || !onExportPDF}
        variant={variant}
        size={size}
        className={`${className} ${variant === 'outline' ? 'border-primary text-primary hover:bg-primary hover:text-background' : ''}`}
      >
        {isExporting ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <FileText className="h-4 w-4 mr-2" />
        )}
        {isExporting ? 'Generating PDF...' : 'Export PDF Report'}
      </Button>
    );
  }

  // Show image export only for public views
  if (isPublicView && !showPowerPoint && !onExportPDF) {
    return (
      <Button
        onClick={() => handleExport('image')}
        disabled={isExporting}
        variant={variant}
        size={size}
        className={`${className} ${variant === 'outline' ? 'border-primary text-primary hover:bg-primary hover:text-background' : ''}`}
      >
        {isExporting ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Image className="h-4 w-4 mr-2" />
        )}
        {isExporting ? 'Generating Image...' : 'Export as Image'}
      </Button>
    );
  }

  // Dropdown for multiple export options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          disabled={isExporting}
          variant={variant}
          size={size}
          className={`${className} ${variant === 'outline' ? 'border-primary text-primary hover:bg-primary hover:text-background' : ''}`}
        >
          {isExporting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {exportType === 'pdf' ? 'Generating PDF...' : 'Generating PowerPoint...'}
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Export Report
              <ChevronDown className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {onExportPDF && (
          <DropdownMenuItem
            onClick={() => handleExport('pdf')}
            disabled={isExporting}
            className="cursor-pointer"
          >
            <FileText className="h-4 w-4 mr-2" />
            Export as PDF
          </DropdownMenuItem>
        )}
        {showPowerPoint && onExportPowerPoint && (
          <DropdownMenuItem
            onClick={() => handleExport('powerpoint')}
            disabled={isExporting}
            className="cursor-pointer"
          >
            <Presentation className="h-4 w-4 mr-2" />
            Export as PowerPoint
          </DropdownMenuItem>
        )}
        {showImageExport && (
          <DropdownMenuItem
            onClick={() => handleExport('image')}
            disabled={isExporting}
            className="cursor-pointer"
          >
            <Image className="h-4 w-4 mr-2" />
            Export as Image (PNG)
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}